// ---------------------------------------------------------------------------
// What the Assessment Agent's elements share: timers that end with the element
// (NDI-239 v2)
//
// Two Lit ReactiveControllers. Each owns one timer and clears it when its
// element leaves the page (hostDisconnected), so a lead card that is
// collapsed, or a page that is left, stops asking the server and changes no
// state after it is gone.
//
//   Poller  calls a function every n ms while it is given a key; a new key
//           starts over, an empty one stops it.
//   Notice  a short message on the element — what a toast said before (they
//           need @adornis/chemistry, which these modules may not reach) — for
//           a few seconds, or until the next one.
// ---------------------------------------------------------------------------

import type { ReactiveController, ReactiveControllerHost } from 'lit';

export class Poller implements ReactiveController {
  private timer: ReturnType<typeof setInterval> | undefined;
  private key = '';

  constructor(host: ReactiveControllerHost) {
    host.addController(this);
  }

  /** Calls `tick` every `ms` while `key` is set; the same key keeps the running timer, '' stops it. */
  run(key: string, ms: number, tick: () => void): void {
    if (key === this.key) return;
    this.stop();
    if (!key) return;
    this.key = key;
    this.timer = setInterval(tick, ms);
  }

  stop(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
    this.key = '';
  }

  /** Whether a timer runs now (for tests). */
  get running(): boolean {
    return this.timer !== undefined;
  }

  hostDisconnected(): void {
    this.stop();
  }
}

export class Notice implements ReactiveController {
  /** What the element shows now; '' when nothing. */
  text = '';
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly host: ReactiveControllerHost) {
    host.addController(this);
  }

  /** Shows `text` for `ms` (4 s by default), replacing what was shown. */
  show(text: string, ms = 4000): void {
    this.clearTimer();
    this.text = text;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.text = '';
      this.host.requestUpdate();
    }, ms);
    this.host.requestUpdate();
  }

  clear(): void {
    this.clearTimer();
    if (this.text) {
      this.text = '';
      this.host.requestUpdate();
    }
  }

  private clearTimer(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  hostDisconnected(): void {
    this.clearTimer();
    this.text = '';
  }
}
